// The in-memory API's DE set-up (PROVISION-IN-PRODUCT; services/api's
// `digitalEmployees.provisioning.get` and `.provision`), with the real routes' access: managers
// only, a DE of another company 404. The fake runs no worker, so every step stays pending until a
// re-run, which marks them done (what the worker would record). Never invented keys or bridges.
import type { OperationOutput, RouteId } from "@companyos/sdk";
import { type Handler, json, notFound, type Principal, problem } from "./fake";
import type { FakeDe } from "./fake-data";

type View = OperationOutput<"digitalEmployees.provisioning.get">;

const STEPS = [
  "budget",
  "agent_key",
  "memory",
  "space",
  "runtime",
  "cos_digest",
  "first_task",
] as const;

const CONNECT_STEPS = [
  "seat",
  "agent_spec",
  "agent_key",
  "endpoint",
  "instance",
  "tool_grant",
  "bridge_unit",
] as const;

export function createProvisioningHandlers(deps: {
  now: () => Date;
  desOf: (companyId: string) => FakeDe[];
}): Partial<Record<RouteId, Handler>> {
  const done = new Map<string, string>();
  // DE-CONNECT: the request is recorded; with no worker every step stays pending.
  const connects = new Map<string, NonNullable<View["connect"]>>();
  const admitted = (p: Principal | null, id: string | undefined): FakeDe | Response => {
    if (!p) return notFound();
    if (!p.manager)
      return problem(403, "forbidden", "Forbidden", "Company managers see a DE's set-up.");
    return deps.desOf(p.company.id).find((d) => d.membership_id === id) ?? notFound();
  };
  const view = (de: FakeDe): View => {
    const at = done.get(de.membership_id) ?? null;
    return {
      membership_id: de.membership_id,
      origin: "new",
      provisioned_at: at,
      steps: STEPS.map((step) => ({
        step,
        state: at ? ("done" as const) : ("pending" as const),
        detail: at ? "Set up" : "Waiting for the worker",
        at,
      })),
      credentials_claimed: false,
      bridge: null,
      connect: connects.get(de.membership_id) ?? null,
    };
  };
  return {
    "digitalEmployees.provisioning.get": ({ principal: p, params }) => {
      const de = admitted(p, params.id);
      return de instanceof Response ? de : json(view(de));
    },
    "digitalEmployees.provision": ({ principal: p, params }) => {
      const de = admitted(p, params.id);
      if (de instanceof Response) return de;
      done.set(de.membership_id, deps.now().toISOString());
      return json(view(de), 202);
    },
    "digitalEmployees.connect": ({ principal: p, params, body }) => {
      const de = admitted(p, params.id);
      if (de instanceof Response) return de;
      const b = (body ?? {}) as Record<string, unknown>;
      const cur = connects.get(de.membership_id);
      const satellite = (b.satellite_id as string | undefined) ?? cur?.satellite_id;
      const instance = (b.instance_id as string | undefined) ?? cur?.instance_id;
      const profile = (b.profile_name as string | undefined) ?? cur?.profile_name;
      if (!satellite || !instance || !profile)
        return problem(422, "validation_failed", "Invalid", "Say where the DE runs.");
      connects.set(de.membership_id, {
        satellite_id: satellite,
        instance_id: instance,
        profile_name: profile,
        adopt: (b.adopt as boolean | undefined) ?? cur?.adopt ?? false,
        connected_at: null,
        steps: CONNECT_STEPS.map((step) => ({
          step,
          state: "pending" as const,
          detail: "Waiting for the worker",
          at: null,
        })),
        spec_version: null,
        endpoint_id: null,
        container_id: null,
        gateway_key_waiting: false,
      });
      return json(view(de), 202);
    },
  };
}
